/**
 * 도메인별 데이터 소스 스위치 — `live`(실물 API) / `demo`(`@ctcells/fixtures`).
 *
 * 🔴 **갈리는 층은 컨테이너 하나다.** fetch 어댑터·MSW 토글로 내리지 않는다.
 *    그렇게 하면 「실물을 때렸는데 목이 답했다」가 되어 실물 검증이라는 목적 자체가 사라진다.
 *    컨테이너가 이 함수를 불러 갈라야, 어느 화면이 목인지 코드에서 한눈에 보인다.
 *
 * ```ts
 * // packages/ui/src/screens/**\/*Route.tsx (컨테이너)
 * const source = dataSourceFor('alarms')
 * const alarms = source === 'demo' ? DEMO_ALARMS : useAlarms().data
 * ```
 *
 * 🔴 기본은 `live` 다. **실물이 비어 있어 화면을 눈으로 확인할 수 없는 도메인만** `demo` 로 내린다.
 *    실물에 값이 차면 아래 표에서 **한 줄씩** `live` 로 되돌린다 — 코드를 고치지 않는다.
 */
import { isRecord, runtimeSection } from './runtimeConfig.ts'

export const DATA_SOURCES = ['live', 'demo'] as const
export type DataSource = (typeof DATA_SOURCES)[number]

/**
 * 스위치 단위. 화면이 아니라 **데이터 도메인**이다 — 한 화면이 두 도메인을 쓰기도 한다
 * (예: 모니터링 = `modules` + `batches` + `alarms`).
 */
export const DATA_DOMAINS = [
  'auth',
  'modules',
  'batches',
  'samples',
  'recipes',
  'alarms',
  'safety',
  'trays',
  'reagents',
  'env',
  'waste',
  'calibration',
  'dispense',
  'preflight',
  'logs',
  'users',
  'sessions',
  'exports',
  'diagnostics',
  'streams',
] as const

export type DataDomain = (typeof DATA_DOMAINS)[number]

/**
 * 기본 배치. 근거는 전부 **2026-09-17 실물 서버 실측**(`docs/api-survey.md`)이다.
 * 추측으로 `demo` 를 늘리지 않는다 — 「데이터가 비어 화면을 확인할 수 없다」가 유일한 사유다.
 */
export const DEFAULT_DATA_SOURCE: Readonly<Record<DataDomain, DataSource>> = {
  auth: 'live',
  modules: 'live',
  batches: 'live',
  samples: 'live',
  safety: 'live',
  trays: 'live',
  preflight: 'live',
  logs: 'live',
  users: 'live',
  sessions: 'live',
  exports: 'live',

  // 🔴 **2026-09-20 `live` 로 올렸다.** 실물이 0건이라 목록이 비어 보이지만 **그게 사실이다** —
  //    목으로 덮으면 「없는 레시피가 있는 것처럼」 보이고, 그 목으로 고른 값으로는 배치를
  //    만들 수 없다(`POST /batches` 는 서버에 실재하는 committed 레시피만 받는다).
  //    새 작업·레시피 신규 화면이 이 도메인을 쓰므로 **목이면 만들 수 없는 조합**이 된다.
  recipes: 'live',

  // 🔴 **2026-09-20 `live` 로 올렸다.** 셋 다 쓰기 경로(`POST`)가 실재해서 **로컬 서버에 실물을
  //    직접 써 넣고** 응답 원문으로 확인했다(회차 표지 `fe-20260920-*`) — 랩웨어 4건 ·
  //    시약 lot 4건(2건 개봉) · 보정 5건. 그 자리에서 실물에서만 드러나는 어긋남 셋을 잡았다:
  //    ⓐ `definition.grid` 가 `[행,열]` 이 아니라 `{rows,columns}`(배열로 보내면 422)
  //    ⓑ 서버가 빈 칸을 `null` 이 아니라 **키째 빼서** 보낸다(`valid_until`)
  //    ⓒ 실물 화면이 보정 임박을 `DEMO_NOW`(고정 시각)로 재고 있었다
  //    ⚠ `labware` 도메인은 2026-10-11 에 걷었다 — 서버 v0.9.4 가 옛 카탈로그를 걷어 화면도 없어졌다
  reagents: 'live',
  calibration: 'live',

  // 🔴 **2026-09-20 `live` 로 올렸다.** 종전 사유(「탭 3종을 가를 `category` 가 없다」)는
  //    **기다려서 풀리는 종류가 아니었다** — `category` 는 REST·구독 어디에도 없고
  //    `alarm_active` 표에 **열조차 없다**(모듈이 실어 보내도 insert 에서 버려진다).
  //    ⇒ 탭 축을 **서버 질의 축(`state`)** 으로 바꾸고 올렸다. 목록·확인·해제 전부 실물로 돈다.
  //    근거 표: `packages/contract/src/alarm.ts` 의 `AlarmEvent` 주석 · BR-08.
  alarms: 'live',

  // 🔴 **2026-09-28 `live` 로 올렸다.** 종전 사유(「배치를 돌려야 찬다」 · BR-34)는 **막힘이 아니었다** —
  //    읽기 경로 `GET /dispense-records` 는 실재하고(operator·admin 200 · viewer 403), 0건이면
  //    화면이 「기록 없음」이라 적는 게 사실이다. demo 로 두면 서랍이 「연결되지 않았습니다」만 말해서
  //    **배치를 돌려 기록이 생겨도 영영 안 보였다.** 들어오는 길은 모듈의 MQTT 발행(IFC-MQ-12) 하나다.
  dispense: 'live',

  // ── 아래부터 demo. 사유는 DEMO_REASON 에 한 줄씩 있다 ──
  env: 'demo',
  waste: 'demo',
  diagnostics: 'demo',

  // 🔴 **2026-09-18 실붙임 확인 후 `live` 로 올렸다.** 유효 토큰으로 `bearer` 서브프로토콜
  //    핸드셰이크가 두 채널 다 열렸고 `events/stream` 은 첫 프레임(`type:"connection"`)까지 왔다.
  //    되돌리려거든 **붙지 않는 것을 먼저 확인**해라 — 목으로 내리면 실물 검증이 사라진다.
  streams: 'live',
}

/**
 * `demo` 인 사유. **개발자용 기록이다 — 화면 문구가 아니다.**
 * 여기 사유가 해소되면 `DEFAULT_DATA_SOURCE` 의 그 줄을 `live` 로 바꾸고 이 줄을 지운다.
 */
export const DEMO_REASON: Readonly<Partial<Record<DataDomain, string>>> = {
  /*
   * 🔴 **아래 셋은 「비어서」가 아니라 «채울 길이 없어서» demo 다**(2026-09-20 전수 실측으로
   *    사유를 고쳤다). 종전 문구 「컬렉션 0건」은 **「값이 들어오기를 기다리면 된다」로 읽혀
   *    틀렸다** — 기다려도 안 들어온다. 값을 넣는 경로가 아예 없거나 우리 손 밖에 있다.
   *    ⇒ 되살리려면 **백엔드 작업이 먼저**다. `docs/backend-requests.csv` BR-32 · BR-33 참조.
   *    (분주 기록은 2026-09-28 `live` 로 올렸다 — 위 기본표 주석)
   */
  env: '센서 등록 경로가 REST·MQTT 어디에도 없다 — env_sensor 레지스트리에 넣는 코드가 시험뿐이다(BR-32)',
  waste: '폐기통 등록 경로가 없다 — 교체 기록 POST 는 «이미 있는» 통에만 붙는다(BR-33)',
  diagnostics:
    '자가 진단 실행 경로가 계약·실물 양쪽에 없다(계약 없음 — docs/api-integration-plan.md §10-2)',
}

/**
 * 🔴 **`demo` 3종의 사유는 서로 다르다.** 「실물이 비었다」 한 낱말로 묶지 마라 —
 *    묶으면 「기다리면 찬다」로 읽히는데 셋 다 기다려도 안 찬다(2026-09-20 전수 실측).
 *
 * | 도메인 | 경로 | 쓰기 경로 | 2026-09-20 실측 |
 * |---|---|---|---|
 * | env | `/env-sensors` | **없음** | 0건 · 레지스트리 등록 경로 0건 |
 * | waste | `/waste-containers` | **없음**(교체 기록만) | 0건 · 통 등록 경로 0건 |
 * | diagnostics | — | — | **경로 자체 없음** |
 *
 * ✅ **올린 것**(같은 날 로컬 서버에 실제로 써 넣고 응답으로 확인)
 *
 * | 도메인 | 경로 | 넣은 것 |
 * |---|---|---|
 * | ~~labware~~ | `POST /labware-definitions` | 4건 — simple 3 · full 1 (⚠ 2026-10-11 도메인째 걷음 · 서버 v0.9.4 창구 소멸) |
 * | reagents | `POST /reagents/lots` (+ `/open`) | 4건 — 개봉 2 · 미개봉 2 |
 * | calibration | `POST /calibrations` | 5건 — 유효기한 있음 4 · 없음 1 |
 *
 * ✅ **알람은 「값을 넣어서」가 아니라 「화면을 응답에 맞춰서」 올렸다**(같은 날)
 *
 * 값은 원래 있었다(4건). 막고 있던 것은 **화면이 응답에 없는 `category` 를 전제한 것**이었고,
 * 그 필드는 기다려도 오지 않는다(`alarm_active` 에 열이 없다). 탭을 서버 질의 축(`state`)으로
 * 바꾸니 그대로 붙었다. 🔴 **「값이 없어서 목」과 「화면이 틀려서 목」은 다른 병이다** —
 * 뒤엣것은 백엔드를 기다릴 일이 아니라 우리가 고칠 일이었다.
 */

export function isDataDomain(value: string): value is DataDomain {
  return (DATA_DOMAINS as readonly string[]).includes(value)
}

function isDataSource(value: unknown): value is DataSource {
  return typeof value === 'string' && (DATA_SOURCES as readonly string[]).includes(value)
}

export interface DataSourceInput {
  /** `VITE_DATA_SOURCE` — `"default=demo,alarms=live"` 꼴 */
  spec?: string | undefined
  /** `window.__CTCELLS_CONFIG__.dataSource` — `{ default: 'live', alarms: 'demo' }` */
  runtime?: unknown
}

/**
 * 우선순위: **도메인 지정 > default 지정 > 내장 기본표**.
 * 알 수 없는 키·값은 **무시한다**(조용히 틀리게 도느니 기본표가 이긴다).
 */
export function resolveDataSourceMap(input: DataSourceInput = {}): Record<DataDomain, DataSource> {
  const resolved: Record<DataDomain, DataSource> = { ...DEFAULT_DATA_SOURCE }

  const entries: [string, unknown][] = []
  if (isRecord(input.runtime)) entries.push(...Object.entries(input.runtime))
  entries.push(...parseSpec(input.spec))

  // default 를 먼저 깔고 도메인 지정으로 덮는다
  for (const [key, value] of entries) {
    if (key === 'default' && isDataSource(value)) {
      for (const domain of DATA_DOMAINS) resolved[domain] = value
    }
  }
  for (const [key, value] of entries) {
    if (key !== 'default' && isDataDomain(key) && isDataSource(value)) resolved[key] = value
  }

  return resolved
}

function parseSpec(spec: string | undefined): [string, string][] {
  if (!spec) return []
  return spec
    .split(',')
    .map((part) => part.split('='))
    .flatMap(([key, value]) =>
      key !== undefined && value !== undefined
        ? ([[key.trim(), value.trim()]] as [string, string][])
        : [],
    )
}

let cached: Record<DataDomain, DataSource> | undefined

/** 현재 적용된 전체 표. 한 번 계산하고 캐시한다(설정은 기동 중에 바뀌지 않는다) */
export function dataSourceMap(): Readonly<Record<DataDomain, DataSource>> {
  cached ??= resolveDataSourceMap({
    spec: import.meta.env.VITE_DATA_SOURCE,
    runtime: runtimeSection('dataSource'),
  })
  return cached
}

/** 🔴 컨테이너가 부르는 유일한 함수. 화면은 이걸 모른다 */
export function dataSourceFor(domain: DataDomain): DataSource {
  return dataSourceMap()[domain]
}

/** 테스트·개발 도구 전용. 캐시를 버려 다음 호출에서 다시 계산하게 한다 */
export function resetDataSourceCache(): void {
  cached = undefined
}
